import { GroupBase, MultiValueProps } from 'react-select';
import { HTMLProps, RefObject, useMemo } from 'react';
import chroma from 'chroma-js';

import { ReactSelectInstanceRefObject, TReactSelectOption } from '../../custom-react-select.types';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import classNames from 'classnames';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import OptionalComponent from '@ui/optional-component/optional-component';
import { isObject } from 'underscore';
import isColorLightOrDark from '@/utils/colors/is-color-light-or-dark';
import { EPaletteModes } from '@/types/theme-packs.types';
import { ContainerProps } from '@ui/container/container.types';
import { TextProps } from '@ui/text/text/text.types';

type TMultiValueProps = {
  option: TReactSelectOption;
  values: TReactSelectOption[];
  selectRef: ReactSelectInstanceRefObject;
  containerRef: RefObject<HTMLDivElement | null>;
  moreItemsRef: RefObject<HTMLDivElement | null>;
  viewableItems: number;
  tabbedIn: boolean;
  hideUnfocusedMenu: boolean;
  cannotClearOptionsMap?: Map<string | number, TReactSelectOption>;
  props: MultiValueProps<unknown, boolean, GroupBase<unknown>>;
  unfocusedContainerProps?: Partial<ContainerProps>;
  unfocusedImageProps?: HTMLProps<HTMLImageElement>;
  unfocusedTextProps?: Partial<TextProps>;
  unfocusedPlusMoreContainerProps?: Partial<ContainerProps>;
};

const MultiValue = ({
  viewableItems,
  selectRef,
  values,
  tabbedIn,
  option,
  containerRef,
  moreItemsRef,
  hideUnfocusedMenu,
  cannotClearOptionsMap,
  props,
  unfocusedContainerProps,
  unfocusedImageProps,
  unfocusedTextProps,
  unfocusedPlusMoreContainerProps,
}: TMultiValueProps) => {
  const {
    index,
    removeProps,
    innerProps,
    isFocused: isOptionFocused,
    components: { Label },
  } = props;
  const isFirst = index === 0;
  const isLast = index === values.length - 1;
  const isWithinView = index < viewableItems;
  const remainingOptions = values.length - viewableItems;
  const isLastVisibleOption = index === viewableItems - 1;
  const oneViewableItem = viewableItems === 1;

  const select = selectRef?.current;

  const meta = option.meta;
  const image = useMemo(() => {
    if (!meta) return null;
    if (!isObject(meta)) return null;
    if (!('image' in meta) && !('img' in meta)) return null;
    if (typeof meta.image !== 'string' && typeof meta.img !== 'string') {
      return null;
    }
    return meta.image || meta.img;
  }, [meta]);

  const cannotClearOption = useMemo(() => {
    return cannotClearOptionsMap?.has(option.value);
  }, [cannotClearOptionsMap, option]);

  const isWrapperStateFocused = !!select?.state?.isFocused || tabbedIn;
  if (isWrapperStateFocused) {
    return (
      <div {...innerProps}>
        <Container
          background="var(--gray-800)"
          borderColor="var(--gray-700)"
          borderWidth="2px"
          borderRadius="24px"
          padding="0 12px"
          height="32px"
          className="flex fd-row jc-flex-start ai-center S3"
        >
          {image ? (
            <img
              loading="lazy"
              className="radius-4 margin-r-4 image-cover min-height-16 min-width-16"
              src={image}
              height={16}
              width={16}
            />
          ) : null}
          <Text
            whiteSpace="nowrap"
            width="100%"
            clamp={1}
            /* to prevent crazy amounts of scrolling, but cannot get ellipses working without breaking widths */
            // maxWidth={`calc(${containerRef?.current?.firstElementChild?.clientWidth || 580}px)`}
          >
            <Label {...props} />
          </Text>

          {cannotClearOption ? null : (
            <div
              {...removeProps}
              data-remove-item-focused={isOptionFocused}
              className={classNames(
                'truncated-creatable-multi-react-select__remove-icon',
                'flex fd-row jc-center ai-center',
                'padding-2',
                'radius-4',
                'margin-l-4',
                {
                  ['truncated-creatable-multi-react-select__remove-icon-focus']: isOptionFocused,
                }
              )}
            >
              <Icon name={ICONS.CLOSE} size={12} />
            </div>
          )}
        </Container>
      </div>
    );
  }

  /* Unfocused state */
  const valueContainerWidth = containerRef?.current?.firstElementChild?.clientWidth;
  const customHex = isObject(option.meta) ? option.meta?.color : undefined;
  const customRGBA = customHex && chroma.valid(customHex) ? chroma(customHex).alpha(0.8).rgba() : '';
  const customOutline = customRGBA ? `rgba(${customRGBA.join(',')})` : '';
  let additionalOffsets = `${moreItemsRef?.current?.offsetWidth || 50}px - 8px - 8px - 8px - 4px - 2px`;
  if (oneViewableItem) {
    additionalOffsets = '20px';
  }
  return (
    <>
      <Container
        className="truncated-select-item-container S3 padding-v-0 padding-h-12 flex fd-row jc-center ai-center"
        width="auto"
        /* 8px gap, 8px left padding, 8px right padding, 4px min input, 2px some minor padding  */
        maxWidth={`calc(${valueContainerWidth ? `${valueContainerWidth}px` : '100%'} - ${additionalOffsets})`}
        background={customHex || 'var(--gray-800)'}
        {...(customOutline
          ? {
              outline: `2px solid ${customOutline}`,
              borderWidth: '0',
              minHeight: '28px',
              ...(isFirst ? { margin: '0 0 0 2px' } : {}) /* outline creates a cut off, so adjust for it */,
            }
          : {
              borderColor: 'var(--gray-700)',
              borderWidth: '2px',
              minHeight: '32px',
            })}
        borderRadius="24px"
        data-key="select-in-view-item"
        style={{
          ...(hideUnfocusedMenu ? { opacity: 0, pointerEvents: 'none' } : {}),
          ...(isWithinView
            ? {}
            : {
                position: 'fixed',
                zIndex: -1,
                opacity: 0,
                visibility: 'hidden',
                pointerEvents: 'none',
                top: 0,
                left: 0,
              }),
        }}
        {...unfocusedContainerProps}
      >
        {image ? (
          <img
            loading="lazy"
            className="radius-4 margin-r-4 image-cover min-height-16 min-width-16"
            src={image}
            height={16}
            width={16}
            {...unfocusedImageProps}
          />
        ) : null}
        <Text
          maxWidth="100%"
          width="100%"
          clamp={1}
          whiteSpace={isLastVisibleOption ? 'normal' : 'nowrap'}
          className="flex ai-center radius-4"
          wordBreak="break-all"
          {...(customHex
            ? {
                color: isColorLightOrDark(customHex) === EPaletteModes.Light ? '#000000' : '#ffffff',
              }
            : {})}
          {...unfocusedTextProps}
        >
          <Label {...props} />
        </Text>
      </Container>

      <OptionalComponent display={!isWithinView && isLast && remainingOptions > 0}>
        <Container
          borderRadius="100px"
          background="var(--gray-800)"
          borderColor="var(--gray-700)"
          borderWidth="2px"
          padding="0 12px"
          height="32px"
          className="flex ai-center jc-center gap-2 S3"
          minWidth="fit-content"
          ref={moreItemsRef}
          style={{ ...(hideUnfocusedMenu ? { opacity: 0, pointerEvents: 'none' } : {}) }}
          {...unfocusedPlusMoreContainerProps}
        >
          + {remainingOptions}
        </Container>
      </OptionalComponent>
    </>
  );
};

export default MultiValue;
